// SPDX-License-Identifier: AGPL-3.0-or-later
import {useEffect, useRef, useState} from 'react';

interface UseAnimatedImageDecoderOptions {
	src: string | null;
	playing: boolean;
	canvas: HTMLCanvasElement | null;
}
export interface AnimatedImageDecoderState {
	supported: boolean;
	loaded: boolean;
	error: boolean;
	naturalWidth: number;
	naturalHeight: number;
}
const DEFAULT_FRAME_DURATION_MS = 100;
type ImageDecoderInit = {
	data: ReadableStream<Uint8Array> | ArrayBuffer | Uint8Array;
	type: string;
	preferAnimation?: boolean;
};
type ImageDecoderInstance = {
	decode: (options?: {frameIndex?: number; completeFramesOnly?: boolean}) => Promise<{
		image: VideoFrame;
		complete: boolean;
	}>;
	tracks: {
		selectedTrack: {
			animated: boolean;
			frameCount: number;
			repetitionCount: number;
		} | null;
	};
	completed: Promise<void>;
	close: () => void;
};
type ImageDecoderConstructor = {
	new (init: ImageDecoderInit): ImageDecoderInstance;
	isTypeSupported(type: string): Promise<boolean>;
};
const getImageDecoderCtor = (): ImageDecoderConstructor | null => {
	const w = globalThis as unknown as {
		ImageDecoder?: ImageDecoderConstructor;
	};
	return w.ImageDecoder ?? null;
};
const guessMimeFromUrl = (url: string): string => {
	const lower = url.split('?')[0]?.toLowerCase() ?? '';
	if (lower.endsWith('.webp')) return 'image/webp';
	if (lower.endsWith('.gif')) return 'image/gif';
	if (lower.endsWith('.apng') || lower.endsWith('.png')) return 'image/png';
	if (lower.endsWith('.avif')) return 'image/avif';
	return 'image/webp';
};
export function useAnimatedImageDecoder({
	src,
	playing,
	canvas,
}: UseAnimatedImageDecoderOptions): AnimatedImageDecoderState {
	const [state, setState] = useState<AnimatedImageDecoderState>(() => ({
		supported: getImageDecoderCtor() !== null,
		loaded: false,
		error: false,
		naturalWidth: 0,
		naturalHeight: 0,
	}));
	const runnerRef = useRef<{
		kick: () => void;
		cancelled: boolean;
	} | null>(null);
	const playingRef = useRef(playing);
	playingRef.current = playing;
	useEffect(() => {
		const Ctor = getImageDecoderCtor();
		if (!Ctor || !src || !canvas) {
			if (!Ctor) setState((prev) => ({...prev, supported: false}));
			return;
		}
		const ctx = canvas.getContext('2d', {alpha: true});
		if (!ctx) {
			setState((prev) => ({...prev, error: true}));
			return;
		}
		const runner = {cancelled: false, kick: () => {}};
		runnerRef.current = runner;
		let decoder: ImageDecoderInstance | null = null;
		let frameCount = Number.POSITIVE_INFINITY;
		let frameIndex = 0;
		let timer: number | null = null;
		let advancing = false;
		const frameCache = new Map<
			number,
			{
				image: VideoFrame;
				durationMs: number;
			}
		>();
		setState((prev) => ({...prev, loaded: false, error: false, supported: true}));
		const clearTimer = () => {
			if (timer != null) {
				window.clearTimeout(timer);
				timer = null;
			}
		};
		const draw = (image: VideoFrame) => {
			if (runner.cancelled) return;
			const w = image.displayWidth;
			const h = image.displayHeight;
			if (canvas.width !== w) canvas.width = w;
			if (canvas.height !== h) canvas.height = h;
			try {
				ctx.clearRect(0, 0, w, h);
				ctx.drawImage(image, 0, 0, w, h);
			} catch {}
		};
		const getFrame = async (index: number) => {
			const cached = frameCache.get(index);
			if (cached) return cached;
			if (!decoder) return null;
			try {
				const result = await decoder.decode({frameIndex: index, completeFramesOnly: true});
				if (runner.cancelled) {
					result.image.close();
					return null;
				}
				const durationMs = (result.image.duration ?? DEFAULT_FRAME_DURATION_MS * 1000) / 1000;
				const entry = {image: result.image, durationMs};
				frameCache.set(index, entry);
				return entry;
			} catch {
				return null;
			}
		};
		const advance = async () => {
			if (advancing || runner.cancelled) return;
			advancing = true;
			try {
				while (!runner.cancelled && playingRef.current) {
					const next = frameCount === 1 ? 0 : (frameIndex + 1) % frameCount;
					if (frameCount === 1) return;
					const frame = await getFrame(next);
					if (!frame || runner.cancelled || !playingRef.current) return;
					frameIndex = next;
					draw(frame.image);
					await new Promise<void>((resolve) => {
						clearTimer();
						timer = window.setTimeout(resolve, Math.max(16, frame.durationMs));
					});
					if (runner.cancelled || !playingRef.current) return;
				}
			} finally {
				advancing = false;
			}
		};
		runner.kick = () => {
			if (runner.cancelled) return;
			void advance();
		};
		const start = async () => {
			try {
				const response = await fetch(src, {cache: 'force-cache'});
				if (runner.cancelled) return;
				if (!response.ok || !response.body) {
					setState((prev) => ({...prev, error: true}));
					return;
				}
				const type = response.headers.get('content-type') ?? guessMimeFromUrl(src);
				const isSupported = await Ctor.isTypeSupported(type).catch(() => false);
				if (!isSupported) {
					setState((prev) => ({...prev, supported: false}));
					return;
				}
				if (runner.cancelled) return;
				decoder = new Ctor({data: response.body, type, preferAnimation: true});
				await decoder.completed;
				if (runner.cancelled) return;
				const track = decoder.tracks.selectedTrack;
				frameCount = track?.frameCount ?? 1;
				const first = await getFrame(0);
				if (!first || runner.cancelled) return;
				draw(first.image);
				setState({
					supported: true,
					loaded: true,
					error: false,
					naturalWidth: first.image.displayWidth,
					naturalHeight: first.image.displayHeight,
				});
				if (playingRef.current && frameCount > 1) {
					void advance();
				}
			} catch {
				if (!runner.cancelled) setState((prev) => ({...prev, error: true}));
			}
		};
		void start();
		return () => {
			runner.cancelled = true;
			clearTimer();
			frameCache.forEach(({image}) => image.close());
			frameCache.clear();
			decoder?.close();
			if (runnerRef.current === runner) runnerRef.current = null;
		};
	}, [src, canvas]);
	useEffect(() => {
		if (!playing) return;
		runnerRef.current?.kick();
	}, [playing]);
	return state;
}
export interface DecodedImageFrames {
	frames: Array<ImageData>;
	delays: Array<number>;
	width: number;
	height: number;
	ready: boolean;
	error: Error | null;
}
interface UseDecodedImageFramesOptions {
	bytes: Uint8Array | null;
	mime: string | null;
}
const HEIC_MIMES = new Set(['image/heic', 'image/heif']);
export function useDecodedImageFrames({bytes, mime}: UseDecodedImageFramesOptions): DecodedImageFrames {
	const [state, setState] = useState<DecodedImageFrames>(() => ({
		frames: [],
		delays: [],
		width: 0,
		height: 0,
		ready: false,
		error: null,
	}));
	useEffect(() => {
		if (!bytes || !mime) return;
		let cancelled = false;
		(async () => {
			try {
				const result = await decodeAllFrames(bytes, mime);
				if (cancelled) return;
				setState({...result, ready: true, error: null});
			} catch (err) {
				if (cancelled) return;
				setState({
					frames: [],
					delays: [],
					width: 0,
					height: 0,
					ready: false,
					error: err instanceof Error ? err : new Error(String(err)),
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [bytes, mime]);
	return state;
}
async function decodeAllFrames(
	bytes: Uint8Array,
	mime: string,
): Promise<{
	frames: Array<ImageData>;
	delays: Array<number>;
	width: number;
	height: number;
}> {
	const Cls = getImageDecoderCtor();
	const lower = mime.toLowerCase();
	if (HEIC_MIMES.has(lower)) {
		const frame = await decodeStaticViaImage(bytes, lower);
		return {frames: [frame.image], delays: [0], width: frame.width, height: frame.height};
	}
	if (Cls && (await Cls.isTypeSupported(lower).catch(() => false))) {
		return decodeAllFramesViaImageDecoder(bytes, lower, Cls);
	}
	const frame = await decodeStaticViaImage(bytes, lower);
	return {frames: [frame.image], delays: [0], width: frame.width, height: frame.height};
}
async function decodeAllFramesViaImageDecoder(
	bytes: Uint8Array,
	type: string,
	Cls: ImageDecoderConstructor,
): Promise<{
	frames: Array<ImageData>;
	delays: Array<number>;
	width: number;
	height: number;
}> {
	const decoder = new Cls({data: bytes, type, preferAnimation: true});
	try {
		await decoder.completed;
		const track = decoder.tracks.selectedTrack;
		const count = Math.max(1, track?.frameCount ?? 1);
		const frames: Array<ImageData> = [];
		const delays: Array<number> = [];
		let width = 0;
		let height = 0;
		for (let i = 0; i < count; i++) {
			const {image} = await decoder.decode({frameIndex: i, completeFramesOnly: true});
			try {
				const w = image.displayWidth;
				const h = image.displayHeight;
				width = w;
				height = h;
				const canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : null;
				if (!canvas) throw new Error('OffscreenCanvas unavailable for ImageDecoder fallback');
				const ctx = canvas.getContext('2d');
				if (!ctx) throw new Error('failed to acquire 2d context');
				ctx.drawImage(image as unknown as CanvasImageSource, 0, 0);
				const data = ctx.getImageData(0, 0, w, h);
				frames.push(new ImageData(new Uint8ClampedArray(data.data), w, h));
				delays.push((image.duration ?? 0) / 1000);
			} finally {
				image.close();
			}
		}
		return {frames, delays, width, height};
	} finally {
		decoder.close();
	}
}
async function decodeStaticViaImage(
	bytes: Uint8Array,
	mime: string,
): Promise<{
	image: ImageData;
	width: number;
	height: number;
}> {
	if (typeof Image === 'undefined' || typeof URL === 'undefined') {
		throw new Error('static decode unavailable: no Image/URL globals');
	}
	const blob = new Blob([new Uint8Array(bytes)], {type: mime});
	const url = URL.createObjectURL(blob);
	try {
		const img = new Image();
		img.src = url;
		if (typeof img.decode === 'function') {
			await img.decode();
		} else {
			await new Promise<void>((resolve, reject) => {
				img.onload = () => resolve();
				img.onerror = () => reject(new Error(`failed to decode ${mime}`));
			});
		}
		const w = img.naturalWidth;
		const h = img.naturalHeight;
		if (w === 0 || h === 0) throw new Error(`failed to decode ${mime}: zero dimensions`);
		const canvas =
			typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : document.createElement('canvas');
		if (canvas instanceof HTMLCanvasElement) {
			canvas.width = w;
			canvas.height = h;
		}
		const ctx = (canvas as OffscreenCanvas | HTMLCanvasElement).getContext('2d');
		if (!ctx) throw new Error('failed to acquire 2d context for static decode');
		(ctx as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D).drawImage(img, 0, 0);
		const data = (ctx as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D).getImageData(0, 0, w, h);
		return {image: new ImageData(new Uint8ClampedArray(data.data), w, h), width: w, height: h};
	} finally {
		URL.revokeObjectURL(url);
	}
}
